import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatDate } from '../lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Users, ChevronRight, Mail } from 'lucide-react';
import OpenInvoicesEmailDialog from '../components/OpenInvoicesEmailDialog';

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [invoiceClient, setInvoiceClient] = useState(null);
  const navigate = useNavigate();

  const fetchClients = () => {
    const params = search ? `?search=${search}` : '';
    api.get(`/clients${params}`).then(r => {
      setClients(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchClients(); }, [search]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="clients-title">Clients</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your client database</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowCreate(true)} data-testid="add-client-button">
          <Plus className="w-4 h-4 mr-2" /> Add Client
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search clients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="client-search"
        />
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : clients.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No clients yet. Add your first client to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead className="text-right">Open invoices</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/clients/${c.id}`)}>
                    <TableCell className="font-medium text-slate-900">{c.name}</TableCell>
                    <TableCell className="text-slate-600">{c.contact_person || '-'}</TableCell>
                    <TableCell className="text-slate-600">{c.email || '-'}</TableCell>
                    <TableCell className="text-slate-600">{c.phone || '-'}</TableCell>
                    <TableCell className="text-slate-600">{c.city || '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(event) => { event.stopPropagation(); setInvoiceClient(c); }}
                        title="Review and email open invoices"
                      >
                        <Mail className="mr-2 h-4 w-4" /> Open invoices
                      </Button>
                    </TableCell>
                    <TableCell><ChevronRight className="w-4 h-4 text-slate-400" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateClientDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchClients(); }} />
      <OpenInvoicesEmailDialog
        open={Boolean(invoiceClient)}
        onOpenChange={(open) => { if (!open) setInvoiceClient(null); }}
        scopeType="client"
        scope={invoiceClient}
      />
    </div>
  );
}

function CreateClientDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', contact_person: '', email: '', phone: '', address: '', city: '', province: 'BC', postal_code: '' });
  const [saving, setSaving] = useState(false);
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Client name is required'); return; }
    setSaving(true);
    try {
      await api.post('/clients', form);
      toast.success('Client created');
      setForm({ name: '', contact_person: '', email: '', phone: '', address: '', city: '', province: 'BC', postal_code: '' });
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add New Client</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Client / Company Name *</Label>
            <Input name="name" value={form.name} onChange={handleChange} placeholder="Oceanview Condos" data-testid="client-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Contact Person</Label>
              <Input name="contact_person" value={form.contact_person} onChange={handleChange} placeholder="John Smith" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="john@example.ca" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input name="phone" value={form.phone} onChange={handleChange} placeholder="604-555-1234" />
            </div>
            <div className="space-y-2">
              <Label>City</Label>
              <Input name="city" value={form.city} onChange={handleChange} placeholder="Vancouver" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Address</Label>
              <Input name="address" value={form.address} onChange={handleChange} placeholder="123 Main St" />
            </div>
            <div className="space-y-2">
              <Label>Postal Code</Label>
              <Input name="postal_code" value={form.postal_code} onChange={handleChange} placeholder="V6B 1A1" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="create-client-submit">
              {saving ? 'Creating...' : 'Create Client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
